import React, { useEffect, useMemo, useRef, useState } from "react";
import { DisplayFields, SortOption } from "./types";
import "../../styles/filterControls.css";

type Mode = "institution" | "ticker";

/* utils */
const prettyYQ = (yq: string) => {
    const m = /(\d{4})\s*[- ]?\s*Q?([1-4])/i.exec(yq);
    return m ? `${m[1]} · Q${m[2]}` : yq;
};
const useClickOutside = <T extends HTMLElement>(open: boolean, onClose: () => void) => {
    const ref = useRef<T | null>(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);
    return ref;
};

/* -------- Custom popover Select for Sort -------- */
function SelectBox<T extends string>({
                                         label,
                                         options,
                                         value,
                                         onChange,
                                     }: {
    label?: string;
    options: { value: T; label: string }[];
    value: T;
    onChange: (v: T) => void;
}) {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState<number>(() =>
        Math.max(0, options.findIndex((o) => o.value === value))
    );
    const ref = useClickOutside<HTMLDivElement>(open, () => setOpen(false));

    useEffect(() => {
        const idx = options.findIndex((o) => o.value === value);
        if (idx >= 0) setActive(idx);
    }, [value, options]);

    const onKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
        if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen(true);
            return;
        }
        if (!open) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, options.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); onChange(options[active].value); setOpen(false); }
        else if (e.key === "Escape") { setOpen(false); }
    };

    const current = options.find((o) => o.value === value)?.label ?? "";

    return (
        <div ref={ref} style={{ position: "relative" }}>
            {label && <div className="rowTitle">{label}</div>}
            <button
                type="button"
                className="selectBtn"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                onKeyDown={onKeyDown}
            >
                <span>{current}</span>
                <span className="selectCaret" style={{ transform: open ? "rotate(180deg)" : "none" }} />
            </button>

            {open && (
                <div role="listbox" className="selectPop">
                    {options.map((opt, i) => {
                        const selected = opt.value === value;
                        return (
                            <div
                                key={opt.value}
                                role="option"
                                aria-selected={selected}
                                className={`selectItem ${i === active ? "isActive" : ""}`}
                                onMouseEnter={() => setActive(i)}
                                onClick={() => { onChange(opt.value); setOpen(false); }}
                            >
                                {opt.label}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// --- YQPicker (newest -> oldest) ---
function YQPicker({
                      label, options, value, onChange,
                  }: { label: string; options: string[]; value: string; onChange: (v: string) => void }) {
    const [open, setOpen] = useState(false);
    const ref = useClickOutside<HTMLDivElement>(open, () => setOpen(false));

    const optionsDesc = useMemo(() => [...options].reverse(), [options]);

    const grouped = useMemo(() => {
        const byYear = new Map<string, string[]>();
        for (const yq of optionsDesc) {
            const y = /^\d{4}/.exec(yq)?.[0] ?? "—";
            if (!byYear.has(y)) byYear.set(y, []);
            byYear.get(y)!.push(yq);
        }
        return Array.from(byYear.entries()).map(([year, list]) => ({ year, list }));
    }, [optionsDesc]);

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <div className="rowTitle">{label}</div>
            <button className="yq-btn" type="button" onClick={() => setOpen((o) => !o)}>
                <span>{prettyYQ(value)}</span>
                <span
                    style={{
                        borderLeft: "6px solid transparent",
                        borderRight: "6px solid transparent",
                        borderTop: "6px solid #bbb",
                        transform: open ? "rotate(180deg)" : "none",
                        transition: "transform .12s",
                    }}
                />
            </button>
            {open && (
                <div className="yq-pop">
                    {grouped.map(({ year, list }) => (
                        <div key={year} style={{ padding: "4px 2px 10px" }}>
                            <div className="yq-year">{year}</div>
                            <div className="yq-grid">
                                {list.map((yq) => {
                                    const sel = yq === value;
                                    return (
                                        <button
                                            key={yq}
                                            className={`yq-opt ${sel ? "isSel" : ""}`}
                                            onClick={() => { onChange(yq); setOpen(false); }}
                                        >
                                            {prettyYQ(yq)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* -------- main -------- */
export default function FilterControls({
                                           mode,
                                           tickerFilter, setTickerFilter, showTickerFilter,
                                           sortOption, setSortOption,
                                           showPositionsInNewestQuarter, setShowPositionsInNewestQuarter,
                                           showPositionShare, setShowPositionShare,
                                           displayFields, setDisplayFields,
                                           allYearQuarters, pendingFromYQ, pendingToYQ, setPendingFromYQ, setPendingToYQ,
                                           showPositionTypes, setShowPositionTypes,
                                           aggregatePositions, setAggregatePositions,
                                           onFetch, fetchDisabled, hideFetch = false,
                                       }: {
    mode: Mode;
    tickerFilter: string; setTickerFilter: (s: string) => void; showTickerFilter: boolean;

    sortOption: SortOption; setSortOption: (s: SortOption) => void;

    showPositionsInNewestQuarter: boolean; setShowPositionsInNewestQuarter: (v: boolean) => void;

    showPositionShare: boolean; setShowPositionShare: (v: boolean) => void;

    displayFields: DisplayFields; setDisplayFields: (df: DisplayFields) => void;

    allYearQuarters: string[]; pendingFromYQ: string; pendingToYQ: string;
    setPendingFromYQ: (s: string) => void; setPendingToYQ: (s: string) => void;

    showPositionTypes: { [type: string]: boolean }; setShowPositionTypes: (m: { [type: string]: boolean }) => void;

    aggregatePositions: boolean; setAggregatePositions: (v: boolean) => void;

    onFetch: () => void; fetchDisabled?: boolean; hideFetch?: boolean;
}) {
    /* keep From <= To */
    const yqIndex = useMemo(() => {
        const m = new Map<string, number>();
        allYearQuarters.forEach((yq, i) => m.set(yq, i));
        return m;
    }, [allYearQuarters]);

    const onFromChange = (v: string) => {
        const iFrom = yqIndex.get(v) ?? 0;
        const iTo = yqIndex.get(pendingToYQ) ?? 0;
        if (iFrom > iTo) { setPendingFromYQ(v); setPendingToYQ(v); } else setPendingFromYQ(v);
    };
    const onToChange = (v: string) => {
        const iFrom = yqIndex.get(pendingFromYQ) ?? 0;
        const iTo = yqIndex.get(v) ?? 0;
        if (iTo < iFrom) { setPendingFromYQ(v); setPendingToYQ(v); } else setPendingToYQ(v);
    };

    const setDF = (k: keyof DisplayFields) => (checked: boolean) =>
        setDisplayFields({ ...displayFields, [k]: checked });

    const toggleType = (k: "SHARE" | "CALL" | "PUT") =>
        setShowPositionTypes({ ...showPositionTypes, [k]: !showPositionTypes[k] });

    /* presets */
    const applyLastNQ = (n: number) => {
        const toIdx = yqIndex.get(pendingToYQ) ?? allYearQuarters.length - 1;
        const fromIdx = Math.max(0, toIdx - (n - 1));
        onFromChange(allYearQuarters[fromIdx]);
    };
    const applyMax = () => onFromChange(allYearQuarters[0]);

    const sortOptions: { value: SortOption; label: string }[] = [
        { value: "value", label: "Value (Descending)" },
        { value: "ticker", label: "Ticker (A→Z)" },
    ];

    return (
        <div className="toolbarModern">
            <div className="stack">

                {/* Row: Filter by Ticker (Institution mode only) */}
                {showTickerFilter && (
                    <div className="row">
                        <div className="rowTitle">Filter by Ticker</div>
                        <input
                            className="input"
                            placeholder="e.g. NVDA"
                            value={tickerFilter}
                            onChange={(e) => setTickerFilter(e.target.value)}
                        />
                    </div>
                )}

                {/* Row: Sort / Types */}
                <div className="row">
                    <div className="rowTitle">Sort / Types</div>

                    <SelectBox<SortOption>
                        options={sortOptions}
                        value={sortOption}
                        onChange={setSortOption}
                    />

                    {mode === "ticker" && (
                        <div className="seg" title="Aggregate positions">
                            <button
                                className={aggregatePositions ? "isOn" : ""}
                                aria-pressed={aggregatePositions}
                                onClick={() => setAggregatePositions(!aggregatePositions)}
                            >
                                Aggregate
                            </button>
                        </div>
                    )}

                    <div className="seg" title="Position types">
                        <button className={showPositionTypes.SHARE ? "isOn" : ""} onClick={() => toggleType("SHARE")}>Shares</button>
                        <button className={showPositionTypes.CALL ? "isOn" : ""}   onClick={() => toggleType("CALL")}>Calls</button>
                        <button className={showPositionTypes.PUT ? "isOn" : ""}    onClick={() => toggleType("PUT")}>Puts</button>
                    </div>

                    <div className="seg" title="Visibility">
                        <button className={showPositionsInNewestQuarter ? "isOn" : ""} onClick={() => setShowPositionsInNewestQuarter(!showPositionsInNewestQuarter)}>Current</button>
                        <button className={showPositionShare ? "isOn" : ""} onClick={() => setShowPositionShare(!showPositionShare)}>% of Total</button>
                    </div>
                </div>

                {/* Row: Display fields */}
                <div className="row">
                    <div className="rowTitle">Display Fields</div>
                    <div className="chips">
                        <button className={`chip ${displayFields.valueDelta ? "isOn" : ""}`}   onClick={() => setDF("valueDelta")(!displayFields.valueDelta)}>Value Δ</button>
                        <button className={`chip ${displayFields.valuePercent ? "isOn" : ""}`} onClick={() => setDF("valuePercent")(!displayFields.valuePercent)}>Value %</button>
                        <button className={`chip ${displayFields.amountDelta ? "isOn" : ""}`}   onClick={() => setDF("amountDelta")(!displayFields.amountDelta)}>Amount Δ</button>
                        <button className={`chip ${displayFields.amountPercent ? "isOn" : ""}`} onClick={() => setDF("amountPercent")(!displayFields.amountPercent)}>Amount %</button>
                    </div>
                </div>

                <div className="hr" />

                {/* Row: Period pickers */}
                <div className="row">
                    <div className="rowTitle">Select Period</div>

                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                        <YQPicker label="To" options={allYearQuarters} value={pendingToYQ} onChange={onToChange} />
                        <div className="arrow">←</div>
                        <YQPicker label="From" options={allYearQuarters} value={pendingFromYQ} onChange={onFromChange} />
                    </div>

                    {!hideFetch && (
                        <button className="button" onClick={onFetch} disabled={fetchDisabled} style={{ marginLeft:12 }}>
                            Fetch
                        </button>
                    )}
                </div>

                {/* Row: Presets (single horizontal line) */}
                <div className="presetRow">
                    <button className="chip" onClick={() => applyLastNQ(4)}>Last 4Q</button>
                    <button className="chip" onClick={() => applyLastNQ(8)}>Last 8Q</button>
                    <button className="chip" onClick={() => applyLastNQ(12)}>Last 12Q</button>
                    <button className="chip" onClick={applyMax}>Max</button>
                </div>

            </div>
        </div>
    );
}
